import { Button, Cell, DataTable, FormControl, Grid, Icon, TableFooter, Tag, Tooltip, VFlow } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import {
  LotacaoAndEstagioSelectField,
  LotacaoAndEstagioSelectFieldModel,
  LotacaoAndEstagioSelectFieldProps,
} from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import { CpfCnsModel } from 'components/model-CpfCns'
import { useLocalTableSort } from 'components/table'
import { TableBox } from 'components/table/TableBox'
import { usePagination } from 'components/table/usePagination'
import { css } from 'emotion'
import React from 'react'
import { MetaArray } from 'util/metaPath'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { EditableListForm, EditableListFormRenderProps } from 'view/atendimentos/detail/components/EditableListForm'
import { formatNomeProfissional } from 'view/profissional/utils/utils-profissional'

import { sortByNomeProfissional } from './converter-lotacaoEditableTable'
import { lotacaoEditableTableValidator } from './validator-lotacaoEditableTable'

enum LotacaoEditableTableColumnEnum {
  NOME = 'nome',
  CBO = 'cbo',
  INE = 'ine',
  INATIVA = 'inativa',
}

export type LotacaoEditableTableSelectFieldModel = LotacaoAndEstagioSelectFieldModel & { ativo?: boolean }

export interface LotacaoEditableTableModel {
  _id: ID
  lotacao: LotacaoEditableTableSelectFieldModel
}

type LotacaoEditableTableProps = Pick<
  LotacaoAndEstagioSelectFieldProps,
  'required' | 'includeEstagios' | 'excludeProfissionaisIds'
> & {
  name: MetaArray<LotacaoEditableTableModel>
  cpfCnsCidadaosParticipantes?: CpfCnsModel[]
}

export function LotacaoEditableTable(props: LotacaoEditableTableProps) {
  const { name, excludeProfissionaisIds, cpfCnsCidadaosParticipantes, ...rest } = props
  const { acesso } = useAcessoLotacaoOrEstagio()
  const {
    handleSubmit,
    removeItem,
    input: { value },
  } = useEditableListField({ name })

  const allItems: LotacaoEditableTableModel[] = value || []
  const { sortedItems, sort, onSortChange } = useLocalTableSort(allItems, sortFunctions, { defaultOrder: 'reverse' })
  const { paginatedItems, tableProps } = usePagination({ items: sortedItems })

  const renderForm = (renderProps: EditableListFormRenderProps<LotacaoEditableTableModel>) => {
    const { name, handleSubmit, values } = renderProps
    const hasLotacaoSelecionada = !!values?.lotacao

    return (
      <Grid gap={2} style={styles.tableHeader}>
        <Cell size={6}>
          <VFlow>
            <LotacaoAndEstagioSelectField
              name={name.lotacao}
              label='Profissional'
              placeholder='Pesquise por nome, CPF ou CNS'
              municipioId={acesso.municipio.id}
              showNomeSocialLabel={true}
              excludeProfissionaisIds={excludeProfissionaisIds || []}
              // TODO Foundation #21877 - Atividade Coletiva será reestruturada posteriormente para inclusão de estagiários
              canWriteAtendimento={false}
              {...rest}
            />
          </VFlow>
        </Cell>

        <Cell size={2}>
          <FormControl label={<span>&nbsp;</span>}>
            <Tooltip text={!hasLotacaoSelecionada ? 'Informe um profissional para adicionar à lista' : ''}>
              <Button kind='primary' disabled={!hasLotacaoSelecionada} onClick={handleSubmit} size='small'>
                Adicionar
              </Button>
            </Tooltip>
          </FormControl>
        </Cell>
      </Grid>
    )
  }

  const renderButtons = (row: LotacaoEditableTableModel) => (
    <Tooltip text='Excluir'>
      <Button type='button' kind='normal' skin='ghost' size='small' onClick={() => removeItem(row)}>
        <Icon icon='trashOutline' />
      </Button>
    </Tooltip>
  )

  return (
    <TableBox
      header={
        <EditableListForm<LotacaoEditableTableModel>
          render={renderForm}
          onSubmit={handleSubmit}
          validate={lotacaoEditableTableValidator(allItems, cpfCnsCidadaosParticipantes)}
          prefix='new'
        />
      }
    >
      <DataTable<LotacaoEditableTableModel>
        rows={paginatedItems}
        sort={sort}
        onSortChange={onSortChange}
        columns={[
          {
            name: LotacaoEditableTableColumnEnum.NOME,
            header: 'Nome',
            sortable: true,
            render: (row) => formatNomeProfissional(row.lotacao.profissional),
            style: styles.tableRowNome,
          },
          {
            name: LotacaoEditableTableColumnEnum.CBO,
            header: 'CBO',
            render: (row) => row.lotacao.cbo.nome.capitalize(),
            style: styles.tableRowCBO,
          },
          {
            name: LotacaoEditableTableColumnEnum.INE,
            header: 'INE',
            render: (row) => row.lotacao.equipe?.nome ?? 'Sem equipe',
          },
          {
            name: LotacaoEditableTableColumnEnum.INATIVA,
            header: '',
            render: (row) => row.lotacao.ativo === false && <Tag>Lotação inativa</Tag>,
          },
          {
            name: 'buttons',
            render: renderButtons,
            style: styles.tableRowButtons,
          },
        ]}
      />
      <TableFooter style={styles.tableFooter} {...tableProps} />
    </TableBox>
  )
}

const sortFunctions = {
  [LotacaoEditableTableColumnEnum.NOME]: (a: LotacaoEditableTableModel, b: LotacaoEditableTableModel) =>
    sortByNomeProfissional(a.lotacao, b.lotacao),
}

const styles = {
  tableHeader: css`
    padding-block: 0.5rem;
  `,
  tableFooter: css`
    border-top: 0;
  `,
  tableRowNome: css`
    width: 22rem;
  `,
  tableRowCBO: css`
    width: 20rem;
  `,
  tableRowButtons: css`
    width: 3.5rem;
    text-align: right;
  `,
}
